'use client';
import { CSSProperties, ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone as PhoneIcon, ShoppingCart as ShoppingCartIcon } from 'lucide-react';
import Link from 'next/link';

import type { ID, SellableType } from '@nutralab/api';
import { BorderCard, Button, EditorText, Flex, Heading, Price, QuantitySelector, Tag } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import styles from './ProductCard.module.scss';

import { CreateProductOrderModal, type CreateProductOrderModalData } from '@/app/_modals/CreateProductOrderModal';
import { useCart } from '@/app/_providers/CartProvider';
import { useModal } from '@/app/_providers/ModalProvider';
import { usePriceTimer } from '@/shared/hooks';
import { getCartKey, sanitizeHtml } from '@/shared/utils';

interface ProductCardProps extends React.HTMLAttributes<HTMLDivElement> {
    productId: ID;
    productType: SellableType;
    /** The product page; `null` makes the card not clickable. */
    href: string | null;
    name: string;
    description: string | null;
    imageSrc: string;
    price: string;
    oldPrice: string | null;
    priceExpiresAt: string | null;
    reserveOldPrice: boolean;
    reserveTimer: boolean;
    currencySign: string;
    tags: string[];
    cardBorderColor: string | null;
    isUndocumented: boolean;
}

/** A product of the home page list: image, price, description, quick order and add to cart. */
export function ProductCard({
    className,
    style,
    productId,
    productType,
    href,
    name,
    description,
    imageSrc,
    price: initialPrice,
    oldPrice: initialOldPrice,
    priceExpiresAt,
    reserveOldPrice,
    reserveTimer,
    currencySign,
    tags,
    cardBorderColor,
    isUndocumented,
    ...props
}: ProductCardProps): ReactElement {
    const { t } = useTranslation();
    const safeDescription = useMemo(() => (description ? sanitizeHtml(description) : null), [description]);
    const { openModal } = useModal();
    const { items, pendingKeys, addItem, updateQuantity, removeItem } = useCart();
    const cartRef = { type: productType, id: productId };
    const cartItem = items.get(getCartKey(cartRef));
    const isPending = pendingKeys.has(getCartKey(cartRef));
    const { price, oldPrice, secondsLeft } = usePriceTimer({
        price: initialPrice,
        oldPrice: initialOldPrice,
        expiresAt: priceExpiresAt,
    });

    const cardHref = isUndocumented ? null : href;

    const handleAddToCart = (): void => {
        addItem(cartRef);
    };

    const openCreateProductOrderModal = (): void => {
        openModal<CreateProductOrderModalData>({
            ModalComponent: CreateProductOrderModal,
            data: {
                productId,
                productType,
                currencySign,
                imageSrc,
                name: name,
                price: price,
            },
        });
    };

    const cardStyle: CSSProperties | undefined = cardBorderColor
        ? ({ ...style, '--product-card-shadow-color': `#${cardBorderColor}` } as CSSProperties)
        : style;

    return (
        <BorderCard
            className={classNames(
                styles['product-card'],
                cardBorderColor && styles['custom-border'],
                cardHref && styles.clickable,
                className,
            )}
            style={cardStyle}
            {...props}
        >
            <Flex direction="column" className={styles.content}>
                {tags.length > 0 && (
                    <div className={styles.tags}>
                        {tags.map((tag) => (
                            <Tag key={tag} variant="fill" color="soft" size="lg">
                                {tag}
                            </Tag>
                        ))}
                    </div>
                )}

                <div className={classNames(styles['image-wrapper'], 'mb-4')}>
                    <img src={imageSrc} alt={name} loading="lazy" />
                </div>
                <Price
                    className="mb-6"
                    amount={price}
                    oldAmount={oldPrice}
                    currencySign={currencySign}
                    reserveOldPrice={reserveOldPrice}
                    secondsLeft={secondsLeft}
                    timerLabel={t('offer.price-timer.label')}
                    reserveTimer={reserveTimer}
                    icon={<img className={styles.icon} src="/nutrition.svg" alt="" loading="lazy" />}
                    timerSize="lg"
                />

                <div>
                    <Heading weight="medium" size="2xl">
                        {cardHref ? (
                            <Link href={cardHref} className={styles['card-link']}>
                                {name}
                            </Link>
                        ) : (
                            name
                        )}
                    </Heading>

                    {safeDescription && <EditorText value={safeDescription} className="mt-4" />}
                </div>

                <Flex wrap="wrap" gap={4} className={classNames(styles.actions, 'mt-auto')}>
                    <div className={styles['quick-order']}>
                        <Button
                            isFullWidth
                            variant="outlined"
                            iconLeft={<PhoneIcon className={classNames(styles['phone-icon'], 'mr-2')} />}
                            onClick={openCreateProductOrderModal}
                        >
                            {t('landing.product-card.quick-order')}
                        </Button>
                    </div>

                    <div className={styles['cart-slot']}>
                        <div
                            className={classNames(
                                styles['cart-slot-item'],
                                styles['quantity-slot'],
                                !cartItem && styles['is-hidden'],
                            )}
                        >
                            <QuantitySelector
                                labels={{
                                    decrease: t('quantity-selector.decrease'),
                                    increase: t('quantity-selector.increase'),
                                    quantity: t('quantity-selector.quantity'),
                                }}
                                value={cartItem?.quantity ?? 1}
                                min={0}
                                disabled={isPending}
                                className={styles.quantity}
                                onChange={(quantity) =>
                                    quantity === 0 ? removeItem(cartRef) : updateQuantity(cartRef, quantity)
                                }
                            />
                        </div>
                        <div className={classNames(styles['cart-slot-item'], cartItem && styles['is-hidden'])}>
                            <Button
                                isFullWidth
                                variant="fill"
                                disabled={isPending}
                                onClick={handleAddToCart}
                                iconLeft={<ShoppingCartIcon />}
                            >
                                {t('landing.product-card.add-to-cart')}
                            </Button>
                        </div>
                    </div>
                </Flex>
            </Flex>
        </BorderCard>
    );
}
